<template>
  <VaSidebar v-model="writableVisible" :width="sidebarWidth" :color="color" minimized-width="0" class="app-sidebar">
    <div class="sidebar-content">
      <VaAccordion v-model="value" multiple>
        <template v-for="(route, index) in navigationRoutes.routes">
          <!-- Standalone client selector when not on a client route -->
          <div
            v-if="route.headerName === 'menuHeaders.client-admin' && !isClientUriSegmentInPathname"
            :key="`client-selector-${index}`"
            class="px-4 py-3"
            @mousedown.stop
            @pointerdown.stop
            @pointerup.stop
            @click.stop
            @keydown.stop
          >
            <!-- add group header name (kept consistent with other sections) -->
            <div v-if="route.headerName" class="text-xs text-secondary pb-3">
              {{ t(route.headerName) }}
            </div>

            <select v-model="selectedClientCode" class="w-full py-2" aria-label="Client">
              <option value="" disabled>
                {{ t('Select Client') }}
              </option>
              <option v-for="client in availableClients" :key="client.clientCode" :value="client.clientCode">
                {{ client.clientName }}
              </option>
            </select>
          </div>

          <!-- Normal collapsible navigation for all other routes (and for client-admin when a client route is active) -->
          <VaCollapse v-else :key="`nav-collapse-${index}`">
            <template #header="{ value: isCollapsed }">
              <!-- add group header name -->
              <div
                v-if="route.headerName"
                :class="isDisplayMenuItem(route) ? 'block' : 'hidden'"
                class="px-4 py-3 text-xs text-secondary"
              >
                {{ t(route.headerName) }}
              </div>

              <VaSidebarItem
                :active="routeHasActiveChild(route)"
                :active-color="activeColor"
                :text-color="textColor(route)"
                :aria-label="`${route.children ? 'Open category ' : 'Visit'} ${t(route.displayName)}`"
                role="button"
                hover-opacity="0.10"
                :class="getMenuItemClass(route)"
                :disabled="route.disabled || disabledMenuItems[route.name]"
                @click="
                  !(route.disabled || disabledMenuItems[route.name]) &&
                  !getRouteFor(route) &&
                  !route.children &&
                  navigateTo(route)
                "
              >
                <component
                  :is="getRouteFor(route) ? 'router-link' : 'div'"
                  :to="getRouteFor(route)"
                  class="block w-full"
                  style="text-decoration: none; color: inherit"
                  @click="getRouteFor(route) && closeSidebarIfMobile()"
                >
                  <VaSidebarItemContent class="py-3 pr-2 pl-4">
                    <VaIcon
                      v-if="route.meta.icon"
                      aria-hidden="true"
                      :name="route.meta.icon"
                      size="20px"
                      :color="iconColor(route)"
                    />
                    <VaSidebarItemTitle class="flex justify-between items-center leading-5 font-semibold">
                      {{ t(route.displayName) }}
                      <VaIcon v-if="route.children" :name="arrowDirection(isCollapsed)" size="20px" />
                    </VaSidebarItemTitle>
                  </VaSidebarItemContent>
                </component>
              </VaSidebarItem>
              <!-- if route.addSeparator is true, add a separator -->
              <hr v-if="route.addSeparator" class="mt-5" :class="isDisplayMenuItem(route) ? 'block' : 'hidden'" />
            </template>
            <template #body>
              <div v-for="(childRoute, index2) in route.children" :key="index2">
                <VaSidebarItem
                  :active="isActiveChildRoute(childRoute)"
                  :active-color="activeColor"
                  :class="getMenuItemClass(childRoute)"
                  :text-color="textColor(childRoute, route)"
                  :aria-label="`Visit ${t(route.displayName)}`"
                  hover-opacity="0.10"
                  :disabled="childRoute.disabled || disabledMenuItems[childRoute.name]"
                  @click="
                    !(childRoute.disabled || disabledMenuItems[childRoute.name]) &&
                    !getRouteFor(childRoute) &&
                    navigateTo(childRoute)
                  "
                >
                  <component
                    :is="getRouteFor(childRoute) ? 'router-link' : 'div'"
                    :to="getRouteFor(childRoute)"
                    class="block w-full"
                    style="text-decoration: none; color: inherit"
                    @click.stop="
                      getRouteFor(childRoute) &&
                      (childRoute.disabled || disabledMenuItems[childRoute.name]
                        ? $event.preventDefault()
                        : closeSidebarIfMobile())
                    "
                  >
                    <VaSidebarItemContent class="py-3 pr-2 pl-11">
                      <VaSidebarItemTitle class="leading-5 font-semibold">
                        {{ t(childRoute.displayName) }}
                      </VaSidebarItemTitle>
                    </VaSidebarItemContent>
                  </component>
                </VaSidebarItem>
              </div>
            </template>
          </VaCollapse>
        </template>
      </VaAccordion>
    </div>

    <!-- Mobile User Account Section -->
    <div v-if="mobile" class="mobile-account-section">
      <AppNavbarActions class="app-sidebar__actions" :is-mobile="mobile" />

      <!-- Environment Badge (shown at bottom of account section) -->
      <div v-if="notPrdEnvironment || isPWAdmin" class="px-4 py-1.5 flex items-center justify-center gap-2">
        <VaBadge v-if="isPWAdmin" text="PW Admin" color="primary" />
        <span v-if="notPrdEnvironment" class="text-red-600 text-xs font-semibold uppercase">
          {{ environmentLabel }}
        </span>
      </div>
    </div>

    <!-- Footer: Labvigator logo and version -->
    <div class="sidebar-footer">
      <div class="flex items-center justify-center gap-1.5">
        <span class="text-[10px] text-[var(--va-secondary)] opacity-60">Powered by</span>
        <RouterLink v-if="currentClient" to="/" aria-label="Visit home page">
          <img src="/icon/labvigator-landscape-clear.png" alt="Labvigator Logo" class="h-3.5" />
        </RouterLink>
        <span v-else class="text-[10px] text-[var(--va-secondary)] opacity-60">Labvigator</span>
        <span class="text-[10px] text-[var(--va-secondary)] opacity-40">v{{ appVersion }}</span>
      </div>
    </div>
  </VaSidebar>
</template>
<script lang="ts">
import { defineComponent, watch, ref, computed } from 'vue'
import { RouteRecordRaw, useRoute, useRouter } from 'vue-router'

import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

import navigationRoutes, { type INavigationRoute } from './NavigationRoutes'
import { useAppConfigStore } from '../../stores/app-config-store'
import { useMsal } from '../../azure/useMsal'
import { Client } from '../../interfaces/client'
import { routes } from '../../router'
import { navigateToRoute } from '../../utils/navigate-to-route'
import { getPWRoles } from '../../azure/token-utils'
import { useAuthStore } from '@/stores/auth-store'
import { useGlobalStore } from '@/stores/global-store'
import piniaStores from '@/stores'
import AppNavbarActions from '../navbar/components/AppNavbarActions.vue'

export default defineComponent({
  name: 'Sidebar',
  components: {
    AppNavbarActions,
  },
  props: {
    visible: { type: Boolean, default: true },
    mobile: { type: Boolean, default: false },
  },
  emits: ['update:visible'],

  setup: (props, { emit }) => {
    const appVersion = import.meta.env.VITE_APP_VERSION
    const environment = import.meta.env.VITE_APP_ENVIRONMENT
    const { getColor, colorToRgba } = useColors()
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const globalStore = useGlobalStore()

    // Close sidebar when on mobile after clicking a navigation item
    const closeSidebarIfMobile = () => {
      if (props.mobile) {
        globalStore.updateSettings({ isSidebarMinimized: true })
      }
    }

    const value = ref<boolean[]>([])
    const environmentLabel = computed(() => environment?.toUpperCase() ?? 'DEV')
    const notPrdEnvironment = computed(() => environmentLabel.value !== 'PRD')

    const writableVisible = computed({
      get: () => props.visible,
      set: (v: boolean) => emit('update:visible', v),
    })

    const isActiveChildRoute = (child: INavigationRoute) => route.name === child.name

    const routeHasActiveChild = (section: INavigationRoute) => {
      // directly check with current route
      const currentRoute = router.currentRoute.value

      if (currentRoute.name === section.name) {
        return true
      }

      if (section.children) {
        return section.children.some((child) => currentRoute.name === child.name)
      }

      return false
    }

    const setActiveExpand = () => {
      value.value = navigationRoutes.routes.map((route: INavigationRoute) => routeHasActiveChild(route))
    }

    const sidebarWidth = computed(() => (props.mobile ? '100vw' : '280px'))
    const color = computed(() => getColor('background-secondary'))
    const activeColor = computed(() => colorToRgba(getColor('focus'), 0.1))

    const iconColor = (route: INavigationRoute) => (routeHasActiveChild(route) ? 'primary' : 'secondary')
    const textColor = (route: INavigationRoute, parent?: INavigationRoute) => {
      if (route.devOnly || (parent && parent.devOnly)) {
        return 'secondary'
      }
      return routeHasActiveChild(route) ? 'primary' : 'textPrimary'
    }
    const arrowDirection = (state: boolean) => (state ? 'va-arrow-up' : 'va-arrow-down')

    watch(() => route.fullPath, setActiveExpand, { immediate: true })

    // useAppConfigStore
    const appConfigStore = useAppConfigStore()

    const { instance, clientRoles } = useMsal()

    // current client
    const currentClient = ref<Client | null>(null)

    const selectedClientCode = computed<string>({
      get: () => currentClient.value?.clientCode ?? '',
      set: (nextClientCode: string) => {
        if (!nextClientCode) {
          currentClient.value = null
          return
        }

        const nextClient = availableClients.value.find((c) => c.clientCode === nextClientCode) ?? null
        if ((currentClient.value?.clientCode ?? null) !== (nextClient?.clientCode ?? null)) {
          currentClient.value = nextClient
        }
      },
    })

    // available clients
    const availableClients = ref<Client[]>([])

    // --- Auth Store ---
    const authStore = useAuthStore(piniaStores)

    const currentClientUriSegment = computed(() => {
      const raw = route.params.clientCode
      return typeof raw === 'string' ? raw : undefined
    })

    // initialize current client by uri segment and appConfigStore's CLIENT_URL_SEGMENT_CLIENT_DATA_MAP
    const initializeCurrentClient = () => {
      const clientUriSegment = currentClientUriSegment.value

      // all clients
      const allClients = Object.values(appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP ?? {}) as Partial<Client>[]

      // use clientRoles to set availableClients
      const nextAvailableClients = clientRoles.value?.['pw']?.includes('ADMIN')
        ? ((allClients as Client[]) ?? [])
        : (((allClients.filter((client) => Object.keys(clientRoles.value ?? {}).includes(client.clientCode ?? '')) as
            | Client[]
            | undefined) ?? []) as Client[])

      const currentCodes = (availableClients.value ?? []).map((c) => c.clientCode).join('|')
      const nextCodes = (nextAvailableClients ?? []).map((c) => c.clientCode).join('|')
      if (currentCodes !== nextCodes) {
        availableClients.value = nextAvailableClients
      }

      // routeClientCode is client code from uri segment
      const routeClientCode = clientUriSegment
        ? appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP?.[clientUriSegment]?.clientCode
        : undefined

      // set current client to route client code
      const nextCurrentClient = availableClients.value.find((client) => client.clientCode === routeClientCode) ?? null
      if ((currentClient.value?.clientCode ?? null) !== (nextCurrentClient?.clientCode ?? null)) {
        currentClient.value = nextCurrentClient
      }
    }

    // initializeCurrentClient()
    // onMounted(() => {
    //   initializeCurrentClient()
    // })

    // watch clientRoles changes, initialize currentClient and availableClients
    watch(
      () => clientRoles.value,
      () => {
        // initialize currentClient and availableClients
        initializeCurrentClient()
      },
      { immediate: true },
    )

    // isClientUriSegmentInPathname
    const isClientUriSegmentInPathname = computed(() => {
      const clientUriSegment = currentClientUriSegment.value
      if (!clientUriSegment) return false
      return !!appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP?.[clientUriSegment]
    })

    // watch currentClient, update route to match client code
    // watch currentClient selection, update route to match client code
    watch(
      () => currentClient.value?.clientCode ?? null,
      (afterClientCode, beforeClientCode) => {
        if (!afterClientCode || afterClientCode === beforeClientCode) return

        // appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP is map of uri segment to client data
        const clientUriSegment = Object.keys(appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP ?? {}).find(
          (key) => appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP[key].clientCode === afterClientCode,
        )

        // Avoid re-navigating if we're already on the selected client segment.
        if (currentClientUriSegment.value === clientUriSegment) return

        if (clientUriSegment) {
          router.push({ name: 'client-home', params: { clientCode: clientUriSegment } })
        }
      },
      { flush: 'post' },
    )

    const navigateTo = (iRoute: INavigationRoute) => {
      closeSidebarIfMobile()
      navigateToRoute({
        route: iRoute,
        router,
      })
    }

    // Helper function to generate route path for router-link
    const getRouteFor = (route: INavigationRoute) => {
      if (route.children) {
        // if route has children, return undefined (no navigation)
        return undefined
      }

      // if iRoute has clientUriPath, navigate to clientUriPath
      // Note: clientUriPath can be an empty string for client-home, so check if property exists
      if (route.clientUriPath !== undefined) {
        // extract client segment uri from window location pathname
        const clientSegmentUri = window.location.pathname.split('/')[1]

        // If no client segment, don't create a route (prevents "Missing required param" error)
        if (!clientSegmentUri || clientSegmentUri === 'home') {
          return undefined
        }

        return {
          name: route.name,
          params: { clientCode: clientSegmentUri },
        }
      }
      // else navigate to route name
      else {
        return { name: route.name }
      }
    }

    const isDisplayMenuItem = (iRoute: INavigationRoute) => {
      // log route.name
      // console.log('[AppSidebar] isDisplayMenuItem route.name', iRoute.name)

      // if the environment is not dev, do not display devOnly routes
      if (environment !== 'dev' && iRoute.devOnly) {
        return false
      }

      // do not display if clientUriPath is specified and current client is not set
      // Note: clientUriPath can be an empty string for client-home, so check if property exists
      if (iRoute.clientUriPath !== undefined && !currentClient.value) {
        return false
      }

      // Check subscription feature gating (for Lab Admin only; PW Admin always sees all)
      if (iRoute.requiredFeature && currentClient.value) {
        // Check if the analytics module is enabled via environment flag
        if (
          iRoute.requiredFeature === 'basicAnalytics' &&
          import.meta.env.VITE_FEATURE_ANALYTICS_MODULE_ENABLED === 'false'
        ) {
          return false
        }

        const idTokenClaims = authStore.idTokenClaims
        const pwRoles = getPWRoles(idTokenClaims) as string[]
        const isPWAdmin = pwRoles?.includes('pw:ADMIN')

        if (!isPWAdmin) {
          // Look up client subscription features from the app config map
          const clientSegment = currentClientUriSegment.value
          const clientData = clientSegment ? appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP?.[clientSegment] : null
          const features = clientData?.subscriptionFeatures
          if (!features || !features[iRoute.requiredFeature]) {
            return false
          }
        }
      }

      // if route contains clientUriPath, check if current client is set
      // recursively find the route from routes
      const findRoutePath = (routes: RouteRecordRaw[], name: string): RouteRecordRaw[] | undefined => {
        for (const route of routes) {
          if (route.name === name) {
            return [route]
          }
          if (route.children) {
            const foundRoutePath = findRoutePath(route.children, name)
            if (foundRoutePath) {
              return [route, ...foundRoutePath]
            }
          }
        }
      }

      const foundRoutes = findRoutePath(routes, iRoute.name)
      const leafRoute = foundRoutes?.[foundRoutes.length - 1]

      // log foundRoutes and leafRoute
      // console.log('[AppSidebar] foundRoutes', foundRoutes, 'leafRoute', leafRoute)

      // find any clientCodes of parent routes
      const parentClientCodes: string[] =
        foundRoutes?.map((route) => (route.meta?.clientCodes as string[]) ?? []).flat() ?? []

      // log parentClientCodes
      // console.log('[AppSidebar] parentClientCodes', parentClientCodes)

      // if found route, check the meta.requiredRoles
      // const requiredRoles = leafRoute?.meta?.requiredRoles as string[]
      const requiredRoles: string[] =
        foundRoutes?.map((route) => (route.meta?.requiredRoles as string[]) ?? []).flat() ?? []

      // if requiredRoles is not set, display the menu item
      if (!requiredRoles || requiredRoles.length === 0) {
        return true
      }

      // if requiredRoles is set, check if idTokenClaims.pwRoles contains requiredRoles
      const idTokenClaims = authStore.idTokenClaims
      const pwRoles = getPWRoles(idTokenClaims) as string[]

      // log idTokenClaims and pwRoles
      // console.log('[AppSidebar] idTokenClaims', idTokenClaims)
      // console.log('[AppSidebar] pwRoles', pwRoles)

      // if pwRoles is not set, do not display the menu item
      if (!pwRoles) {
        return false
      }

      const targetClientCodes = [
        'pw',
        ...(parentClientCodes.length > 0 ? parentClientCodes : [currentClient.value?.clientCode]),
      ]
        // dedup
        .filter((clientCode, index, self) => self.indexOf(clientCode) === index)

      // log targetClientCodes
      // console.log('[AppSidebar] targetClientCodes', targetClientCodes)

      // filter the pwRoles first. pwRoles is in the format of "clientCode:role"

      const clientPwRoles = pwRoles
        .map((pwRole) => pwRole.split(':'))
        // .filter((clientCodeAndRole) => clientCodeAndRole[0] === currentClient.value?.clientCode)
        .filter((clientCodeAndRole) => targetClientCodes.includes(clientCodeAndRole[0]))
        .map((clientCodeAndRole) => clientCodeAndRole[1])

      // log clientPwRoles
      // console.log('[AppSidebar] clientPwRoles', clientPwRoles)

      // if clientPwRoles is not set, do not display the menu item
      if (!clientPwRoles || clientPwRoles.length === 0) {
        return false
      }

      // check if clientPwRoles contains requiredRoles
      const result = requiredRoles.some((requiredRole) => clientPwRoles.includes(requiredRole))
      // log final return value
      // console.log('[AppSidebar] isDisplayMenuItem result', result)
      return result
    }

    // --- Pure helper function (now expects precomputed values) ---
    function isMenuItemDisabled({
      allowWhenOffline = false,
      isPWAdmin = false,
      isLabAdmin = false,
      isClientOffline = false,
    }: {
      allowWhenOffline: boolean | undefined
      isLabAdmin: boolean
      isClientOffline: boolean
      isPWAdmin: boolean
    }) {
      // If the user is a PW admin, they can access everything
      if (isPWAdmin) {
        return false
      }
      if (isLabAdmin && isClientOffline && !allowWhenOffline) {
        return true
      }
      return false
    }

    // Extracted refs/computeds for all dependencies
    const clientDataMapRef = computed(() => appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP)
    const idTokenClaimsRef = computed(() => authStore.idTokenClaims)
    const isClientOffline = computed(() => currentClient.value?.status !== 'active')
    const pwRoles = computed(() => getPWRoles(idTokenClaimsRef.value) as string[])
    const isLabAdmin = computed(() => {
      if (!currentClient.value || !pwRoles.value) return false
      return pwRoles.value.includes(`${currentClient.value.clientCode}:ADMIN`)
    })
    const isPWAdmin = computed(() => {
      if (!pwRoles.value) return false
      return pwRoles.value.includes('pw:ADMIN')
    })

    // --- Add computed for disabled state map ---
    const disabledMenuItems = computed(() => {
      const map: Record<string, boolean> = {}
      const processRoute = (route: INavigationRoute) => {
        if (route.name && currentClient.value) {
          // Find the route in the router config
          const findRoutePath = (routes: RouteRecordRaw[], name: string): RouteRecordRaw[] | undefined => {
            for (const route of routes) {
              if (route.name === name) {
                return [route]
              }
              if (route.children) {
                const foundRoutePath = findRoutePath(route.children, name)
                if (foundRoutePath) {
                  return [route, ...foundRoutePath]
                }
              }
            }
          }
          const foundRoutes = findRoutePath(routes, route.name)
          const leafRoute = foundRoutes?.[foundRoutes.length - 1]
          const allowWhenOffline = leafRoute?.meta?.labAdminAllowWhenOffline
          map[route.name] = isMenuItemDisabled({
            allowWhenOffline: allowWhenOffline as boolean | undefined,
            isPWAdmin: isPWAdmin.value,
            isLabAdmin: isLabAdmin.value,
            isClientOffline: isClientOffline.value,
          })
        }
        if (route.children) {
          route.children.forEach(processRoute)
        }
      }
      navigationRoutes.routes.forEach(processRoute)
      return map
    })
    // --- End add ---

    const getMenuItemClass = (route: INavigationRoute) => {
      if (!isDisplayMenuItem(route)) return 'hidden opacity-50 pointer-events-none'

      const isDisabled =
        route.disabled === true ||
        (route.name &&
          currentClient.value &&
          (() => {
            // Find the route in the router config
            const findRoutePath = (routes: RouteRecordRaw[], name: string): RouteRecordRaw[] | undefined => {
              for (const route of routes) {
                if (route.name === name) {
                  return [route]
                }
                if (route.children) {
                  const foundRoutePath = findRoutePath(route.children, name)
                  if (foundRoutePath) {
                    return [route, ...foundRoutePath]
                  }
                }
              }
            }
            const foundRoutes = findRoutePath(routes, route.name)
            const leafRoute = foundRoutes?.[foundRoutes.length - 1]
            const allowWhenOffline = leafRoute?.meta?.labAdminAllowWhenOffline
            return isMenuItemDisabled({
              allowWhenOffline: allowWhenOffline as boolean | undefined,
              isLabAdmin: isLabAdmin.value,
              isClientOffline: isClientOffline.value,
              isPWAdmin: isPWAdmin.value,
            })
          })())

      if (isDisabled) {
        // If route has children, keep it clickable for expand/collapse but show as disabled
        if (route.children && route.children.length > 0) {
          return 'block opacity-50'
        }
        // For routes without children, disable interaction completely
        return 'block opacity-50 pointer-events-none'
      }

      return 'block'
    }

    return {
      writableVisible,
      sidebarWidth,
      value,
      color,
      activeColor,
      navigationRoutes,
      routeHasActiveChild,
      isActiveChildRoute,
      t,
      iconColor,
      textColor,
      arrowDirection,
      currentClient,
      selectedClientCode,
      availableClients,
      navigateTo,
      getRouteFor,
      isDisplayMenuItem,
      appVersion,
      environmentLabel,
      notPrdEnvironment,
      isPWAdmin,
      // pwRoles,
      isClientUriSegmentInPathname,
      getMenuItemClass,
      isMenuItemDisabled,
      disabledMenuItems,
      closeSidebarIfMobile,
    }
  },
})
</script>

<style lang="scss">
.app-sidebar {
  // Ensure the sidebar uses flexbox for proper layout
  .va-sidebar__menu {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .sidebar-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .mobile-account-section {
    flex-shrink: 0;
  }

  .sidebar-footer {
    flex-shrink: 0;
    padding: 0.5rem;
  }
}
</style>
